import React, { useState, useRef } from 'react';
import { Screen } from '../App';
import { getUrgencyStrategy, getAccountRole } from '../api';
import { containsRiskLanguage } from '../safety';
import SafetyInterstitial from '../components/SafetyInterstitial';
import AiDisclaimer from '../components/AiDisclaimer';

interface Props { navigate: (s: Screen) => void; }

export default function UrgencyMode({ navigate }: Props) {
  const parent = getAccountRole() === 'parent';
  // 'safety' is the hard-coded, non-AI routing screen — reached either by choosing
  // the crisis path or by the risk-language short-circuit before any API call.
  const [mode, setMode] = useState<'choose' | 'quick' | 'safety'>('choose');
  const [input, setInput] = useState('');
  const [strategy, setStrategy] = useState('');
  const [strategy2, setStrategy2] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const recRef = useRef<any>(null);

  const startRec = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR(); r.continuous = true; r.interimResults = true;
    r.onresult = (e: any) => setInput(Array.from(e.results).map((x: any) => x[0].transcript).join(''));
    r.start(); recRef.current = r; setRecording(true);
  };
  const stopRec = () => { if (recRef.current) { recRef.current.stop(); recRef.current = null; } setRecording(false); };

  const getQuickHelp = async (getSecond = false) => {
    if (!input.trim()) return;
    // Risk-language short-circuit: route to human help, make NO API call.
    if (containsRiskLanguage(input)) { stopRec(); setMode('safety'); return; }
    setLoading(true);
    try {
      if (!getSecond) {
        const s = await getUrgencyStrategy(input);
        setStrategy(s);
      } else {
        const s = await getUrgencyStrategy(input + ' Give a completely different strategy from the first one.');
        setStrategy2(s);
      }
    } catch {
      if (!getSecond) setStrategy(parent
        ? "Right now, lower your voice to just above a whisper and move slowly toward your child. Say: 'I can see this is really hard. I'm not going anywhere. When you're ready, I'm here.' Then wait without demanding anything."
        : "Right now, lower your voice to just above a whisper and move slowly toward the student. Say: 'I can see this is really hard. I'm not going anywhere. When you're ready, I'm here.' Then wait without demanding anything.");
      else setStrategy2(parent
        ? "In this moment, get down to your child's level from a calm, safe distance and simply say their name softly. You don't need to fix it yet — your steady, calm presence is the intervention."
        : "In this moment, create physical space by calmly moving other students away. Then sit or crouch at the student's level from a safe distance and simply say their name softly. Your calm presence is the intervention.");
    } finally { setLoading(false); }
  };

  // SAFETY ROUTING (hard-coded, no model call, no generated text)
  if (mode === 'safety') return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          Dashboard
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Get help now</span>
        <div style={{ width: 60 }} />
      </div>
      <SafetyInterstitial onBack={() => { setMode('choose'); setInput(''); }} />
    </div>
  );

  if (loading) return (
    <div className="coral-screen" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 48, height: 48, border: '3px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 20 }} />
      <p style={{ color: 'white', fontWeight: 700, fontSize: 18 }}>Getting help now...</p>
    </div>
  );

  // MODE CHOOSE
  if (mode === 'choose' && !strategy) return (
    <div className="coral-screen">
      <button onClick={() => navigate('dashboard')} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, width: 'auto', alignSelf: 'flex-start', marginBottom: 24, border: 'none', cursor: 'pointer' }}>← Back</button>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <p style={{ fontSize: 36, marginBottom: 12 }}>🚨</p>
        <h1 style={{ color: 'white', fontSize: 26, marginBottom: 8 }}>I'm here.</h1>
        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16, lineHeight: 1.6, marginBottom: 28 }}>Take one breath. What kind of help do you need right now?</p>
        <button onClick={() => setMode('quick')} style={{ background: 'white', color: 'var(--coral)', padding: '18px', borderRadius: 12, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer', marginBottom: 12, textAlign: 'left' }}>
          ⚡ Quick Strategy<br /><span style={{ fontSize: 13, fontWeight: 400 }}>One thing I can try in the next 5 minutes</span>
        </button>
        <button onClick={() => setMode('safety')} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '18px', borderRadius: 12, fontSize: 16, fontWeight: 700, border: '2px solid rgba(255,255,255,0.4)', cursor: 'pointer', textAlign: 'left' }}>
          🛟 Safety concern<br /><span style={{ fontSize: 13, fontWeight: 400 }}>{parent ? 'A child may be in danger — get the right people now' : 'A student may be in danger — get the right people now'}</span>
        </button>
      </div>
    </div>
  );

  // QUICK MODE INPUT
  if (mode === 'quick' && !strategy) return (
    <div className="coral-screen">
      <button onClick={() => setMode('choose')} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, width: 'auto', alignSelf: 'flex-start', marginBottom: 24, border: 'none', cursor: 'pointer' }}>← Back</button>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 18, lineHeight: 1.6, marginBottom: 24 }}>Tell me what is happening right now.</p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
          <textarea value={input} onChange={e => setInput(e.target.value)} placeholder={parent ? 'What is happening right now with your child?' : 'What is happening right now with this student?'} style={{ background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)', color: 'white', borderRadius: 12, padding: '14px 16px', fontSize: 15, minHeight: 100 }} />
          <button onClick={() => recording ? stopRec() : startRec()} style={{ width: 52, height: 52, borderRadius: '50%', background: recording ? 'white' : 'rgba(255,255,255,0.2)', color: recording ? 'var(--coral)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: 'none', cursor: 'pointer' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /></svg>
          </button>
        </div>
        {recording && <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginBottom: 10 }}>🔴 Recording...</p>}
        <button onClick={() => getQuickHelp()} disabled={!input.trim()} style={{ background: 'white', color: 'var(--coral)', padding: '16px', borderRadius: 12, fontSize: 16, fontWeight: 700, border: 'none', cursor: 'pointer' }}>Get Help Now</button>
      </div>
    </div>
  );

  // QUICK STRATEGY RESPONSE
  if (mode === 'quick' && strategy) return (
    <div className="coral-screen">
      <button onClick={() => navigate('dashboard')} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, width: 'auto', alignSelf: 'flex-start', marginBottom: 24, border: 'none', cursor: 'pointer' }}>← Back</button>
      <div style={{ flex: 1 }}>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Try this RIGHT NOW:</p>
        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '20px', marginBottom: 20 }}>
          <p style={{ color: 'white', fontSize: 18, lineHeight: 1.7, fontWeight: 500 }}>{strategy}</p>
        </div>
        {strategy2 && (
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 14, padding: '20px', marginBottom: 20 }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginBottom: 8 }}>Alternative strategy:</p>
            <p style={{ color: 'white', fontSize: 16, lineHeight: 1.7 }}>{strategy2}</p>
          </div>
        )}
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 15, marginBottom: 16 }}>Is this helping?</p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <button onClick={() => navigate('dashboard')} style={{ flex: 1, background: 'rgba(255,255,255,0.9)', color: 'var(--teal)', padding: '12px', borderRadius: 10, fontWeight: 700, border: 'none', cursor: 'pointer' }}>✓ Yes</button>
          <button onClick={() => getQuickHelp(true)} style={{ flex: 1, background: 'rgba(255,255,255,0.2)', color: 'white', padding: '12px', borderRadius: 10, fontWeight: 600, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>I need more</button>
        </div>
        <button onClick={() => navigate('talk')} style={{ background: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'underline', width: '100%', padding: '10px', border: 'none', cursor: 'pointer' }}>Go deeper on this situation →</button>
      </div>
      <button onClick={() => navigate('dashboard')} style={{ background: 'white', color: 'var(--coral)', padding: '14px', borderRadius: 12, fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer', marginTop: 16 }}>I'm okay now</button>
      <div style={{ color: 'rgba(255,255,255,0.85)' }}><AiDisclaimer navigate={navigate} /></div>
    </div>
  );

  return null;
}
